import React, { useState, useMemo, useEffect } from 'react';
import ReinnoireCard from '../Shared/ReinnoireCard';
import { listProcBopi } from '../../services/bopi';
import * as borderouSvc from '../../services/borderou';
import DateField from '../Shared/DateField';
import { fmtDateRO } from '../../utils/dateFmt';
import ProceduraFileSection from '../Shared/ProceduraFileSection';
import { FilterPill } from '../Shared/FilterPill';
import './Tracker.css';

/* ── Tarife OSIM default (01.01.2026) ── */
const TARIFE_DEFAULT = {
  depunere: 51,
  publicare_abn: 152,
  publicare_color: 508,
  ind_verbala: 559,
  ind_fig_abn: 711,
  ind_fig_color: 1117,
  ind_suplimentar: 254,
  col_verbala: 1982,
  col_fig_abn: 2287,
  col_fig_color: 2795,
  col_suplimentar: 356,
  reclasificare: 152,
  certificat: 254,
};

export const CLASE_NISA = [
  [1,'Produse chimice pentru industrie si stiinta'],
  [2,'Vopsele, lacuri, coloranti, rasini'],
  [3,'Cosmetice, parfumerie, produse de curatare'],
  [4,'Uleiuri si grasimi industriale, combustibili, lumanari'],
  [5,'Produse farmaceutice, de uz medical si veterinar'],
  [6,'Metale comune, materiale de constructie metalice'],
  [7,'Masini, masini-unelte, motoare (nu pt. vehicule)'],
  [8,'Scule si instrumente de mana actionate manual'],
  [9,'Aparate stiintifice, electrice, software, IT'],
  [10,'Aparate si instrumente medicale, chirurgicale, dentare'],
  [11,'Aparate de iluminat, incalzire, racire, gatit, ventilatie'],
  [12,'Vehicule; aparate de locomotie terestra, aeriana, navala'],
  [13,'Arme de foc, munitie, explozibili, artificii'],
  [14,'Metale pretioase, bijuterii, ceasuri'],
  [15,'Instrumente muzicale'],
  [16,'Hartie, carton, tiparituri, papetarie'],
  [17,'Cauciuc, materiale plastice semiprelucrate, izolatoare'],
  [18,'Piele si imitatii, bagaje, selarie, umbrele'],
  [19,'Materiale de constructie nemetalice'],
  [20,'Mobilier, oglinzi, rame; produse din lemn, pluta'],
  [21,'Ustensile si recipiente de menaj si bucatarie, sticlarie'],
  [22,'Franghii, plase, corturi, fibre textile brute'],
  [23,'Fire de uz textil'],
  [24,'Tesaturi si produse textile, lenjerie de casa'],
  [25,'Imbracaminte, incaltaminte, articole pt. acoperirea capului'],
  [26,'Dantele, broderii, panglici, nasturi, accesorii par'],
  [27,'Covoare, presuri, linoleum, tapet'],
  [28,'Jocuri, jucarii, articole de sport, decoratiuni Craciun'],
  [29,'Carne, peste, lactate, conserve, uleiuri alimentare'],
  [30,'Cafea, ceai, faina, paine, patiserie, condimente'],
  [31,'Produse agricole si horticole brute, animale vii, plante'],
  [32,'Bere, ape minerale, bauturi nealcoolice, sucuri'],
  [33,'Bauturi alcoolice (cu exceptia berii)'],
  [34,'Tutun, articole pentru fumatori, chibrituri'],
  [35,'Publicitate, management afaceri, comert cu amanuntul/online'],
  [36,'Servicii financiare, asigurari, imobiliare'],
  [37,'Constructii, reparatii, servicii de instalatii'],
  [38,'Telecomunicatii'],
  [39,'Transport, ambalare si depozitare, organizare calatorii'],
  [40,'Prelucrarea si tratarea materialelor'],
  [41,'Educatie, instruire, divertisment, activitati sportive/culturale'],
  [42,'Servicii stiintifice si tehnologice, IT, design, software'],
  [43,'Servicii de alimentatie publica, cazare temporara'],
  [44,'Servicii medicale, veterinare, igiena, infrumusetare'],
  [45,'Servicii juridice, de securitate, servicii personale'],
];

/* ── Helpers ── */
const todayMid = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };
const parseISO = (s) => (s ? new Date(s + 'T00:00:00') : null);
const addMonths = (d, m) => { const x = new Date(d); x.setMonth(x.getMonth() + m); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const fmtRO = (d) => d ? d.toLocaleDateString('ro-RO', { day:'2-digit', month:'2-digit', year:'numeric' }) : '—';
const daysLeft = (d) => d ? Math.ceil((d - todayMid()) / 86400000) : null;
const fmtLei = (n) => Number(n).toLocaleString('ro-RO') + ' lei';
const fmtBorderouDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).split('-');
  return d ? `${d}.${m}.${y}` : iso;
};

/* ── emptyRecord export ── */
export function emptyRecord() {
  return {
    denumire: '',
    natura: 'individuala',
    tip: 'verbala',
    color: 'abn',
    clase: {},
    reclasificare: false,
    depusa: false,
    oficiu: 'OSIM',
    numar: '',
    dataDepunere: '',
    dataInregistrare: '',
    dataExpirare: '',
    dataPrimaPublicare: '',
    decizieNumar: '',
    decizieDataReceptie: '',
    decizieDataBOPI: '',
    certificatPrimit: false,
    transmisClient: false,
    marcaInregistrata: false,
    viennaCode: '',
    observatii: '',
    achitat_taxa_depunere_inreg: false,  // legacy — split into the two below
    achitat_taxa_depunere: false,
    achitat_taxa_publicare: false,
    achitat_taxa_examinare: false,
    achitat_eliberare_certificat: false,
    borderouri: [],
    borderat_keys: [],
    termenMandatar: '',
    achitat_taxa_mandatar: false,
    termenModificare: '',
    achitat_taxa_modificare: false,
    reinnoire_anuntat_client: false,
    reinnoire_depusa: false,
    reinnoire_ciclu_expirare: null,
  };
}

/* ── Fee calculation ── */
function calcTaxe(r, T) {
  const nClase = Object.keys(r.clase).length;
  const nEff = Math.max(1, nClase);
  const isColor = r.tip !== 'verbala' && r.color === 'color';
  const isVerbala = r.tip === 'verbala';

  let examBase, suplim;
  if (r.natura === 'colectiva') {
    examBase = isVerbala ? T.col_verbala : isColor ? T.col_fig_color : T.col_fig_abn;
    suplim = T.col_suplimentar;
  } else {
    examBase = isVerbala ? T.ind_verbala : isColor ? T.ind_fig_color : T.ind_fig_abn;
    suplim = T.ind_suplimentar;
  }
  const examSupl = (nEff - 1) * suplim;
  const examinare = examBase + examSupl;
  const publicare = isColor ? T.publicare_color : T.publicare_abn;
  const reclas = r.reclasificare ? T.reclasificare : 0;
  const initiale = T.depunere + publicare + examinare + reclas;
  const certificat = T.certificat;
  return { nClase, nEff, isColor, isVerbala, depunere: T.depunere, publicare, examBase, examSupl, examinare, reclas, initiale, certificat, total: initiale + certificat, suplim };
}

/* ── Status / timeline ── */
function deriveStatus(r) {
  const bopi = parseISO(r.decizieDataBOPI);
  const opozitieEnd = bopi ? addMonths(bopi, 2) : null;
  const opozitieEnded = opozitieEnd ? todayMid() >= opozitieEnd : false;
  const certDeadline = opozitieEnd ? addDays(opozitieEnd, 30) : null;

  // Already-registered mark (imported / long-registered): registration or expiry
  // date on file, or the explicit flag. Everything up to registration counts as done.
  const registered = !!(r.marcaInregistrata || r.dataInregistrare);

  const steps = [
    { key:'depunere',   label:'Depunere',           done: registered || !!r.depusa,              date: parseISO(r.dataDepunere) },
    { key:'publicare',  label:'Prima publicare',     done: registered || !!r.dataPrimaPublicare,  date: parseISO(r.dataPrimaPublicare) },
    { key:'admitere',   label:'Decizie admitere',    done: registered || !!bopi,                  date: bopi },
    { key:'opozitie',   label:'Opozitie (2 luni)',   done: registered || opozitieEnded,           date: opozitieEnd },
    { key:'plata',      label:'Plata certificat',    done: registered || r.certificatPrimit,      date: certDeadline },
    { key:'certificat', label:'Certificat primit',   done: r.certificatPrimit,                    date: null },
    { key:'client',     label:'Transmis client',     done: r.transmisClient,                      date: null },
  ];

  let alert;
  if (registered) {
    alert = (r.certificatPrimit && !r.transmisClient)
      ? { label:'De transmis certificatul catre client', sev:'act' }
      : { label:'Marca inregistrata', sev:'done' };
  } else if (!r.depusa) {
    alert = { label:'In pregatire — de depus', sev:'neutral' };
  } else if (!r.dataPrimaPublicare) {
    alert = { label:'Asteptare prima publicare in BOPI', sev:'info' };
  } else if (!bopi) {
    alert = { label:'Asteptare decizie de admitere', sev:'info' };
  } else if (!opozitieEnded) {
    alert = { label:'Perioada de opozitie (2 luni)', date: opozitieEnd, sev:'watch' };
  } else if (!r.certificatPrimit) {
    alert = { label:'Transmitere dovada plata certificat OSIM', date: certDeadline, sev:'act' };
  } else if (!r.transmisClient) {
    alert = { label:'De transmis certificatul catre client', sev:'act' };
  } else {
    alert = { label:'Procedura finalizata', sev:'done' };
  }

  if (alert.date) {
    const dl = daysLeft(alert.date);
    alert.daysLeft = dl;
    if (dl < 0) alert.sev = 'overdue';
  }

  return { steps, alert, opozitieEnd, opozitieEnded, certDeadline, bopi };
}

/* ════════════════════════ Component ════════════════════════ */
export default function MarcaOSIMTracker({ data, onChange, onPersisted, procId, showToast, pendingFiles, onPendingFilesChange }) {
  const [r, setR] = useState(data);
  const [tarife, setTarife] = useState(TARIFE_DEFAULT);
  const [showTarife, setShowTarife] = useState(false);
  const [bopiAparitii, setBopiAparitii] = useState([]);

  useEffect(() => {
    if (!procId) return;
    listProcBopi(procId).then(d => setBopiAparitii(d.aparitii || [])).catch(() => {});
  }, [procId]);

  const set = (patch) => {
    const next = { ...r, ...patch };
    setR(next);
    onChange(next);
  };

  const taxe = useMemo(() => calcTaxe(r, tarife), [r, tarife]);
  const status = useMemo(() => deriveStatus(r), [r]);
  const isVerbala = r.tip === 'verbala';

  /* ── Borderou taxe OSIM ── */
  const [genBorderou, setGenBorderou] = useState(false);
  const [reserveSold, setReserveSold] = useState(null);   // firm-wide 'sold rezerva', null until loaded
  const borderatKeys = r.borderat_keys || [];
  const borderouri   = r.borderouri || [];

  useEffect(() => {
    borderouSvc.getReserve().catch(() => {});
    return borderouSvc.subscribeReserve(setReserveSold);
  }, []);

  const BORDEROU_FEES = [
    { key: 'depunere',   flag: 'achitat_taxa_depunere',        legacy: 'achitat_taxa_depunere_inreg', label: 'Achitat taxă depunere',        amount: taxe.depunere },
    { key: 'publicare',  flag: 'achitat_taxa_publicare',       legacy: 'achitat_taxa_depunere_inreg', label: 'Achitat taxă publicare',       amount: taxe.publicare },
    { key: 'examinare',  flag: 'achitat_taxa_examinare',                                              label: 'Achitat taxă examinare',       amount: taxe.examinare },
    { key: 'certificat', flag: 'achitat_eliberare_certificat',                                        label: 'Achitat eliberare certificat', amount: taxe.certificat },
  ];
  const feeChecked = (f) => {
    if (r[f.flag] !== undefined && r[f.flag] !== null) return !!r[f.flag];
    if (f.legacy) return !!r[f.legacy];
    return false;
  };
  const borderouForKey = (key) =>
    borderouri.find(b => (b.lines || []).some(l => l.key === key));

  const pendingFees  = BORDEROU_FEES.filter(f => feeChecked(f) && !borderatKeys.includes(f.key));
  const pendingTotal = pendingFees.reduce((s, f) => s + (Number(f.amount) || 0), 0);
  const REZERVA_MSG  = 'Nu sunt suficienti bani in rezerva pentru a emite borderou. Depune bani si incearca din nou.';
  const insufficient = reserveSold !== null && pendingTotal > 0 && pendingTotal > reserveSold;

  const handleGenerateBorderou = async () => {
    if (!procId) { showToast('Salvează procedura înainte de a genera borderoul.'); return; }
    if (!pendingFees.length) { showToast('Bifează cel puțin o taxă nouă (neinclusă într-un borderou).'); return; }
    if (insufficient) { showToast(REZERVA_MSG); return; }
    setGenBorderou(true);
    try {
      const res = await borderouSvc.generateBorderou(procId, {
        lines: pendingFees.map(f => ({ key: f.key, suma: f.amount })),
        marca: r.denumire || '',
        nr_depozit: r.numar || '',
        clase: Object.keys(r.clase || {}).map(Number),
      });
      // merge only borderou state so unsaved tracker edits aren't lost;
      // the backend already persisted it, so also bubble it up to the parent
      const merged = {
        ...r,
        borderouri: res.procedura.data.borderouri || [],
        borderat_keys: res.procedura.data.borderat_keys || [],
      };
      setR(merged);
      onChange(merged);
      if (onPersisted) onPersisted(merged);
      await borderouSvc.downloadBorderou(procId, res.borderou.id, res.borderou.filename);
      showToast(`✓ Borderou generat · sold rezervă: ${res.sold} RON`);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Eroare la generarea borderoului');
    } finally {
      setGenBorderou(false);
    }
  };

  const toggleClasa = (nr) => {
    const c = { ...r.clase };
    if (c[nr] !== undefined) delete c[nr]; else c[nr] = '';
    set({ clase: c });
  };
  const setTermeni = (nr, val) => set({ clase: { ...r.clase, [nr]: val } });

  return (
    <div className="tracker">
      <AlertBanner status={status} />
      <Timeline steps={status.steps} />

      {/* Identificare */}
      <TrackerCard title="Identificarea marcii">
        <div className="field">
          <label>Denumire marca</label>
          <input type="text" value={r.denumire} onChange={e => set({ denumire: e.target.value })} placeholder="ex: Aurora Style" />
        </div>
        <div className="f-row">
          <div className="field">
            <label>Natura cererii</label>
            <SegControl value={r.natura} onChange={v => set({ natura: v })} options={[['individuala','Individuala'],['colectiva','Colectiva / certificare']]} />
          </div>
          <div className="field">
            <label>Tip marca</label>
            <FilterPill value={r.tip} onChange={e => set({ tip: e.target.value })}>
              <option value="verbala">Verbala</option>
              <option value="combinata">Combinata (cuvant + imagine)</option>
              <option value="figurativa">Figurativa (logo/imagine)</option>
              <option value="stilizata">Caractere Stilizate</option>
              <option value="tridimensionala">Tridimensionala (3D)</option>
              <option value="sonora">Sonora</option>
              <option value="alt">Alt tip</option>
            </FilterPill>
          </div>
        </div>
        {!isVerbala && (
          <div className="field">
            <label>Fisiere de reprezentare <span className="f-hint">— logo, imagine, fisier 3D etc.</span></label>
            <ProceduraFileSection procId={procId} showToast={showToast} pendingFiles={pendingFiles} onPendingFilesChange={onPendingFilesChange} />
          </div>
        )}
        {!isVerbala && (
          <div className="field">
            <label>Coduri Vienna <span className="f-hint">— separate prin virgula, ex: 05.03.13, 26.03.01</span></label>
            <input type="text" value={r.viennaCode || ''} onChange={e => set({ viennaCode: e.target.value })} className="mono" placeholder="ex: 05.03.13, 26.04.07" />
          </div>
        )}
        <div className="field">
          <label>Culoare {isVerbala && <span className="f-hint">— marca verbala este standard (alb-negru)</span>}</label>
          <SegControl
            value={isVerbala ? 'abn' : r.color}
            disabled={isVerbala}
            onChange={v => set({ color: v })}
            options={[['abn','Alb-negru'],['color','Color']]}
          />
        </div>
      </TrackerCard>

      {/* Clase Nisa */}
      <TrackerCard title={`Clase Nisa (${Object.keys(r.clase).length} selectate)`} collapsible defaultOpen={false}>
        <p className="f-hint mb8">Bifeaza clasele si lipeste lista de produse/servicii per clasa.</p>
        <div className="clase-grid">
          {CLASE_NISA.map(([nr, titlu]) => {
            const sel = r.clase[nr] !== undefined;
            return (
              <div key={nr} className={`clasa-item${sel ? ' clasa-sel' : ''}`}>
                <label className="clasa-label">
                  <input type="checkbox" checked={sel} onChange={() => toggleClasa(nr)} />
                  <span className="clasa-nr">{String(nr).padStart(2,'0')}</span>
                  <span className="clasa-titlu">{titlu}</span>
                </label>
                {sel && (
                  <textarea
                    value={r.clase[nr]}
                    onChange={e => setTermeni(nr, e.target.value)}
                    placeholder={`Produse/servicii pt. clasa ${nr}…`}
                    rows={2}
                    className="clasa-textarea"
                  />
                )}
              </div>
            );
          })}
        </div>
      </TrackerCard>

      {/* Calcul taxe */}
      <TrackerCard title="Calcul taxe OSIM" badge="tarife 01.01.2026">
        {r.oficiu !== 'OSIM' && (
          <p className="tr-note">Oficiu selectat: {r.oficiu}. Calculul de mai jos este pentru taxele nationale OSIM.</p>
        )}
        <table className="fee-table">
          <tbody>
            <FeeRow label="Taxa de depunere" value={taxe.depunere} />
            <FeeRow label={`Taxa de publicare (${taxe.isColor ? 'color' : 'alb-negru'})`} value={taxe.publicare} />
            <FeeRow label={`Examinare pe fond — baza (${r.natura === 'colectiva' ? 'colectiva' : 'individuala'}, ${taxe.isVerbala ? 'verbala' : taxe.isColor ? 'fig./3D color' : 'fig./3D a/n'})`} value={taxe.examBase} />
            {taxe.nEff > 1 && <FeeRow label={`Clase suplimentare (${taxe.nEff - 1} × ${taxe.suplim} lei)`} value={taxe.examSupl} />}
            {r.reclasificare && <FeeRow label="Reclasificare (optional)" value={taxe.reclas} />}
            <FeeRow label="Subtotal la depunere + examinare" value={taxe.initiale} strong />
            <FeeRow label="Eliberare certificat (la admitere, +30 z.)" value={taxe.certificat} muted />
          </tbody>
          <tfoot>
            <tr className="fee-total">
              <td>Total taxe oficiale</td>
              <td>{fmtLei(taxe.total)}</td>
            </tr>
          </tfoot>
        </table>
        {taxe.nClase === 0 && <p className="f-hint mt4">* Selecteaza cel putin o clasa. Calculul afiseaza minimul pentru 1 clasa.</p>}

        <label className="check-label mt8">
          <input type="checkbox" checked={r.reclasificare} onChange={e => set({ reclasificare: e.target.checked })} />
          Solicit reclasificare de catre OSIM (+{tarife.reclasificare} lei)
        </label>

        <div style={{ marginTop: 12, borderTop: '1px solid #e2e8f0', paddingTop: 10 }}>
          <p className="tr-note" style={{ marginBottom: 6 }}>Confirmare plăți taxe</p>
          {BORDEROU_FEES.map(f => {
            const locked = borderatKeys.includes(f.key);
            const b = locked ? borderouForKey(f.key) : null;
            return (
              <div key={f.key} className="osim-fee-row">
                <label className={`check-label${locked ? ' disabled' : ''}`}>
                  <input
                    type="checkbox"
                    disabled={locked}
                    checked={locked ? true : feeChecked(f)}
                    onChange={e => set({ [f.flag]: e.target.checked })}
                  />
                  {f.label}
                  {locked && b && (
                    <span className="osim-fee-lock"> · inclus în borderou din {fmtBorderouDate(b.data)}</span>
                  )}
                </label>
                <span className="osim-fee-amount">{fmtLei(f.amount)}</span>
              </div>
            );
          })}

          {/* ── Borderouri ── */}
          <div className="tr-divider">Borderouri</div>
          {borderouri.length === 0 ? (
            <p className="f-hint" style={{ margin: '2px 0 8px' }}>Niciun borderou generat.</p>
          ) : (
            <ul className="osim-borderou-list">
              {borderouri.map(b => (
                <li key={b.id}>
                  <span>
                    Borderou din {fmtBorderouDate(b.data)} · {fmtLei(b.total)}
                    <span className="f-hint"> — {(b.lines || []).map(l => l.label).join(', ')}</span>
                  </span>
                  <button type="button" className="btn-link" onClick={() => borderouSvc.downloadBorderou(procId, b.id, b.filename)}>
                    Descarcă
                  </button>
                </li>
              ))}
            </ul>
          )}
          {reserveSold !== null && (
            <p className="f-hint" style={{ margin: '2px 0 6px' }}>
              Sold rezervă: <strong>{fmtLei(reserveSold)}</strong>
              {pendingTotal > 0 && <> · necesar borderou: {fmtLei(pendingTotal)}</>}
            </p>
          )}
          {insufficient && (
            <p className="tr-alert sev-overdue" style={{ margin: '0 0 8px', padding: '8px 10px', fontSize: 12.5 }}>
              {REZERVA_MSG}
            </p>
          )}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            style={{ marginTop: 4 }}
            disabled={genBorderou || pendingFees.length === 0 || insufficient}
            onClick={handleGenerateBorderou}
          >
            {genBorderou
              ? 'Se generează...'
              : `Generează borderou${pendingFees.length ? ` (${pendingFees.length} ${pendingFees.length === 1 ? 'taxă' : 'taxe'})` : ''}`}
          </button>

          <div className="f-row" style={{ marginTop: 12 }}>
            <div className="field">
              <label>Termen taxă introducere mandatar</label>
              <DateField value={r.termenMandatar || ''} onChange={v => set({ termenMandatar: v })} />
            </div>
            <div className="field" style={{ alignSelf: 'flex-end', paddingBottom: 4 }}>
              <label className="check-label">
                <input type="checkbox" checked={!!r.achitat_taxa_mandatar} onChange={e => set({ achitat_taxa_mandatar: e.target.checked })} />
                Achitat
              </label>
            </div>
          </div>
          <div className="f-row">
            <div className="field">
              <label>Termen taxă modificare date titular</label>
              <DateField value={r.termenModificare || ''} onChange={v => set({ termenModificare: v })} />
            </div>
            <div className="field" style={{ alignSelf: 'flex-end', paddingBottom: 4 }}>
              <label className="check-label">
                <input type="checkbox" checked={!!r.achitat_taxa_modificare} onChange={e => set({ achitat_taxa_modificare: e.target.checked })} />
                Achitat
              </label>
            </div>
          </div>
        </div>

        <button type="button" className="btn-link mt8" onClick={() => setShowTarife(s => !s)}>
          {showTarife ? '▾' : '▸'} Tarife oficiale (editabile)
        </button>
        {showTarife && <TarifeEditor tarife={tarife} setTarife={setTarife} />}
      </TrackerCard>

      {/* Depunere + monitorizare */}
      <TrackerCard title="Depunere si monitorizare">
        <label className="check-label">
          <input type="checkbox" checked={r.depusa} onChange={e => set({ depusa: e.target.checked })} />
          <strong>Marca a fost depusa</strong>
        </label>
        <p className="f-hint mb8" style={{ marginLeft: 22 }}>La bifare si introducerea numarului, porneste urmarirea publicarii si a termenelor.</p>

        {r.depusa && (
          <div className="dep-section">
            <div className="f-row">
              <div className="field">
                <label>Oficiu</label>
                <FilterPill value={r.oficiu} onChange={e => set({ oficiu: e.target.value })}>
                  <option value="OSIM">OSIM (national)</option>
                  <option value="EUIPO">EUIPO (UE)</option>
                  <option value="WIPO">WIPO (Madrid)</option>
                </FilterPill>
              </div>
              <div className="field">
                <label>{r.oficiu === 'OSIM' ? 'Nr. depozit national' : `Nr. inregistrare ${r.oficiu}`}</label>
                <input type="text" value={r.numar} onChange={e => set({ numar: e.target.value })}
                  placeholder={r.oficiu === 'OSIM' ? 'ex. M 2026 01234' : r.oficiu === 'EUIPO' ? 'ex. 018xxxxxx' : 'ex. 1xxxxxx'}
                  className="mono" />
              </div>
            </div>
            <div className="field">
              <label>Data depunerii</label>
              <DateField value={r.dataDepunere} onChange={v => set({ dataDepunere: v })} />
            </div>

            <div className="tr-divider">Publicare &amp; decizie</div>

            <div className="field">
              <label>Data primei publicari (BOPI)</label>
              <DateField value={r.dataPrimaPublicare} onChange={v => set({ dataPrimaPublicare: v })} />
              <span className="f-hint">Activeaza alerta de monitorizare a admiterii.</span>
            </div>
            <div className="f-row">
              <div className="field">
                <label>Nr. decizie admitere</label>
                <input type="text" value={r.decizieNumar} onChange={e => set({ decizieNumar: e.target.value })} className="mono" />
              </div>
              <div className="field">
                <label>Data receptiei deciziei</label>
                <DateField value={r.decizieDataReceptie} onChange={v => set({ decizieDataReceptie: v })} />
              </div>
              <div className="field">
                <label>Data publicarii in BOPI</label>
                <DateField value={r.decizieDataBOPI} onChange={v => set({ decizieDataBOPI: v })} />
                <span className="f-hint">Porneste termenul de opozitie (2 luni).</span>
              </div>
            </div>

            {/* Aparitii BOPI */}
            {r.oficiu === 'OSIM' && (
              <div style={{ marginTop: 10, marginBottom: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                  Aparitii BOPI
                </div>
                {bopiAparitii.length === 0 ? (
                  <span className="f-hint">Nicio aparitie inregistrata (ruleaza BOPI sync pentru a prelua datele)</span>
                ) : (
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    {bopiAparitii.map(b => (
                      <li key={b.id} style={{ fontSize: 13, color: '#374151', marginBottom: 3 }}>
                        Nr. BOPI {b.nr_bopi}/{b.an_bopi}
                        {b.data_bopi ? ` — ${fmtDateRO(b.data_bopi)}` : ''}
                        {b.capitol ? `, cap. ${b.capitol}` : ''}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {status.opozitieEnd && (
              <div className="f-row">
                <DeadlineChip label="Sfarsit opozitie (2 luni)" date={status.opozitieEnd} />
                <DeadlineChip label="Termen plata certificat (+30 z.)" date={status.certDeadline} act />
              </div>
            )}

            <div className="tr-divider">Finalizare</div>

            <div className="field">
              <label>Data inregistrarii marcii</label>
              <DateField value={r.dataInregistrare || ''} onChange={v => set({ dataInregistrare: v })} />
            </div>
            <div className="field">
              <label>Data expirarii</label>
              <DateField value={r.dataExpirare || ''} onChange={v => set({ dataExpirare: v })} />
            </div>

            <label className="check-label">
              <input type="checkbox" checked={r.certificatPrimit} onChange={e => set({ certificatPrimit: e.target.checked })} />
              Certificatul de inregistrare a fost primit
            </label>
            <label className={`check-label${!r.certificatPrimit ? ' disabled' : ''}`}>
              <input type="checkbox" disabled={!r.certificatPrimit} checked={r.transmisClient} onChange={e => set({ transmisClient: e.target.checked })} />
              Certificatul a fost transmis catre client
            </label>
            <label className="check-label">
              <input type="checkbox" checked={r.marcaInregistrata || false} onChange={e => set({ marcaInregistrata: e.target.checked })} />
              Statusul marcii este inregistrata
            </label>
          </div>
        )}
      </TrackerCard>

            {/* ── Reinnoire ── */}
      <ReinnoireCard r={r} set={set} TrackerCard={TrackerCard} />

      {/* Observatii */}
      <TrackerCard title="Observatii interne">
        <textarea className="tr-textarea" value={r.observatii} onChange={e => set({ observatii: e.target.value })} rows={3} placeholder="Note de dosar…" />
      </TrackerCard>

      {/* Documente inregistrare */}
      <TrackerCard title="Documente inregistrare">
        <ProceduraFileSection
          procId={procId}
          showToast={showToast}
          pendingFiles={pendingFiles}
          onPendingFilesChange={onPendingFilesChange}
          tipOptions={['Duplicat Cerere inregistrare', 'Copie Certificat Inregistrare']}
          keyPrefix="inreg"
        />
      </TrackerCard>
    </div>
  );
}

/* ── Sub-components ── */
function AlertBanner({ status }) {
  const { alert } = status;
  return (
    <div className={`tr-alert sev-${alert.sev}`}>
      <strong>{alert.label}</strong>
      {alert.date && (
        <span className="tr-alert-date">
          Termen: {fmtRO(alert.date)}
          {typeof alert.daysLeft === 'number' && (
            alert.daysLeft < 0
              ? ` · depasit cu ${Math.abs(alert.daysLeft)} zile`
              : ` · ${alert.daysLeft} zile ramase`
          )}
        </span>
      )}
    </div>
  );
}

function Timeline({ steps }) {
  const activeIdx = steps.findIndex(s => !s.done);
  return (
    <div className="tr-timeline">
      {steps.map((s, i) => {
        const isActive = i === activeIdx;
        const done = s.done;
        return (
          <React.Fragment key={s.key}>
            {i > 0 && <span className={`tl-line${done || steps[i-1].done ? ' tl-line-done' : ''}`} />}
            <div className="tl-step">
              <span className={`tl-dot${done ? ' done' : isActive ? ' active' : ''}`}>
                {done ? '✓' : i + 1}
              </span>
              <span className={`tl-label${isActive ? ' active' : done ? ' done' : ''}`}>{s.label}</span>
              {s.date && <span className="tl-date">{fmtRO(s.date)}</span>}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function TrackerCard({ title, badge, children, collapsible = false, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = !collapsible || open;
  return (
    <div className="tr-card">
      <div
        className={`tr-card-head${collapsible ? ' tr-card-head-toggle' : ''}`}
        onClick={collapsible ? () => setOpen(o => !o) : undefined}
      >
        <span className="tr-card-title">{title}</span>
        <div className="tr-card-head-right">
          {badge && <span className="tr-badge">{badge}</span>}
          {collapsible && <span className={`tr-card-arrow${isOpen ? ' tr-card-arrow-open' : ''}`}>›</span>}
        </div>
      </div>
      {isOpen && <div className="tr-card-body">{children}</div>}
    </div>
  );
}

function SegControl({ value, onChange, options, disabled }) {
  return (
    <div className={`seg-ctrl${disabled ? ' seg-disabled' : ''}`}>
      {options.map(([v, label]) => (
        <button key={v} type="button" disabled={disabled}
          className={`seg-btn${value === v ? ' seg-active' : ''}`}
          onClick={() => !disabled && onChange(v)}>
          {label}
        </button>
      ))}
    </div>
  );
}

function FeeRow({ label, value, strong, muted }) {
  return (
    <tr className={strong ? 'fee-strong' : muted ? 'fee-muted' : ''}>
      <td>{label}</td>
      <td className="fee-val">{fmtLei(value)}</td>
    </tr>
  );
}

function DeadlineChip({ label, date, act }) {
  const dl = daysLeft(date);
  const overdue = dl < 0;
  const soon = dl >= 0 && dl <= 14;
  const cls = overdue ? 'chip-overdue' : (act && soon) ? 'chip-act' : 'chip-neutral';
  return (
    <div className={`deadline-chip ${cls}`}>
      <span>{label}</span>
      <span>{fmtRO(date)} · {overdue ? `${Math.abs(dl)}z depasit` : `${dl}z`}</span>
    </div>
  );
}

function TarifeEditor({ tarife, setTarife }) {
  const fields = [
    ['depunere','Depunere'], ['publicare_abn','Publicare a/n'], ['publicare_color','Publicare color'],
    ['ind_verbala','Ind. verbala'], ['ind_fig_abn','Ind. fig./3D a/n'], ['ind_fig_color','Ind. fig./3D color'], ['ind_suplimentar','Ind. clasa supl.'],
    ['col_verbala','Col. verbala'], ['col_fig_abn','Col. fig./3D a/n'], ['col_fig_color','Col. fig./3D color'], ['col_suplimentar','Col. clasa supl.'],
    ['reclasificare','Reclasificare'], ['certificat','Certificat'],
  ];
  return (
    <div className="tarife-editor">
      {fields.map(([k, lbl]) => (
        <label key={k} className="tarife-field">
          {lbl}
          <input type="number" value={tarife[k]}
            onChange={e => setTarife({ ...tarife, [k]: Number(e.target.value) || 0 })} />
        </label>
      ))}
    </div>
  );
}
